import {
  Box3,
  BoxGeometry,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Vector3,
} from "three";
import { Engine } from "../engine/Engine";
import { LENGTH_UNIT_CHANGED_EVENT } from "../core/units/LengthUnit";
import { getLocalBoundingBox } from "../util/GeometryUtil";
import { SimpleMeter } from "./SimpleMeter";
import { SugarModel } from "./SugarModel";
import { SugarModelConfigurator } from "./SugarModelConfigurator";

export class SugarModelDimensions {
  //private _configurator: SugarModelConfigurator;
  private _meters: SimpleMeter[] = [];
  private _show = false;
  private _lastModels: SugarModel[] = [];

  constructor() {
    Engine.activeEngine?.addEventListener("camera-change", (e: any) => {
      this._align(e.detail);
    });
    window.addEventListener(LENGTH_UNIT_CHANGED_EVENT, this._onUnitChanged);
  }

  private readonly _onUnitChanged = (): void => {
    if (this._show && this._lastModels.length) {
      this.update(this._lastModels);
    }
  };

  private _align(camera: OrthographicCamera | PerspectiveCamera) {
    this._meters.forEach((meter) => {
      const meterWorldPosition = meter.getWorldPosition(new Vector3());
      const meterDirection = meter.getWorldDirection(new Vector3());

      const meterWorldDirectionPosition = meterWorldPosition
        .clone()
        .add(meterDirection);

      camera.updateProjectionMatrix();
      camera.updateMatrixWorld();
      camera.updateWorldMatrix(true, true);

      const d = meterWorldDirectionPosition.project(camera);
      const p = meterWorldPosition.project(camera);

      const di = p.x > d.x ? 1 : -1;
      const sd0 = p.clone().sub(d).normalize().multiplyScalar(di);
      const rad = Math.atan2(
        sd0.y,
        sd0.x * (camera instanceof PerspectiveCamera ? camera.aspect : 1),
      );
      meter.labelSprite.material.rotation = rad;

      const zoom = camera.userData.controller.getDistance();
      //meter.visible = p.distanceTo(d) * zoom > .25;
      meter.setZoom(zoom);
    });
  }

  update(models: SugarModel[]) {
    const show = this._show;
    this.hide();
    if (show) {
      this.show(models);
    }
  }

  show(models: SugarModel[], showParts: boolean = true, outerSize?: Vector3) {
    this.hide();
    this._show = true;
    this._lastModels = models;

    models.forEach((model) => {
      const { size, center, box } = this.getSize(model);

      const layoutOuter = outerSize;
      // Bütün modüllerin servis ölçüsü (catalog x/y/z) varsa etiketler
      // modüllerin joint sırasıyla toplanan ölçüden gelir; çizgi modelde kalır.
      const serviceLayout =
        !layoutOuter && model instanceof SugarModelConfigurator
          ? this._layoutFromJoints(model)
          : undefined;
      if (serviceLayout && this.debug && model instanceof SugarModelConfigurator) {
        this._addDebugMeshes(model, serviceLayout.layouts);
      }
      const serviceOuter = serviceLayout && {
        x: serviceLayout.fromService.x ? serviceLayout.size.x : undefined,
        y: serviceLayout.fromService.y ? serviceLayout.size.y : undefined,
        z: serviceLayout.fromService.z ? serviceLayout.size.z : undefined,
      };

      if (model instanceof SugarModelConfigurator) {
        this._addMeterItemsOfConfigurator(
          model,
          center.y + size.y * 0.5 + 0.1,
          showParts,
        );
      }

      const marginSide = 0.15;
      const marginBck = 0.125;
      const marginTop = 0.2;

      // Tanımlı layout size ekseni çizgi uzunluğu ve kenarıdır.
      // Tanımsız eksen birleşik bbox'ta kalır (ör. "140x77" yüksekliği).
      const meterSize = size.clone();
      const meterCenter = center.clone();
      const hasLayoutSize =
        !!layoutOuter &&
        (layoutOuter.x > 0 || layoutOuter.y > 0 || layoutOuter.z > 0);
      if (hasLayoutSize && layoutOuter) {
        if (layoutOuter.x > 0) meterSize.x = layoutOuter.x;
        if (layoutOuter.y > 0) meterSize.y = layoutOuter.y;
        if (layoutOuter.z > 0) meterSize.z = layoutOuter.z;
        if (model instanceof SugarModelConfigurator) {
          const bodyCenter = this._mainBodyCenter(model);
          if (bodyCenter) meterCenter.copy(bodyCenter);
        }
      }

      const _meterX = new SimpleMeter(meterSize.x);
      _meterX.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterX.setSize(meterSize.x);
      _meterX.setSizeLabel(serviceOuter?.x ?? meterSize.x);
      _meterX.position.setX(meterCenter.x - meterSize.x * 0.5);
      _meterX.position.setZ(meterCenter.z - meterSize.z * 0.5 - marginBck);
      _meterX.position.setY(meterCenter.y + meterSize.y * 0.5 + marginTop);
      model.add(_meterX);
      this._meters.push(_meterX);

      const _meterY = new SimpleMeter(meterSize.y);
      _meterY.setDirection(new Vector3(0, 1, 0), new Vector3(1, 0, 0));
      _meterY.setSize(meterSize.y);
      _meterY.setSizeLabel(serviceOuter?.y ?? meterSize.y);
      _meterY.position.setX(meterCenter.x - meterSize.x * 0.5 - marginSide);
      _meterY.position.setZ(meterCenter.z - meterSize.z * 0.5 - marginSide);
      model.add(_meterY);
      this._meters.push(_meterY);

      //const nextJoint = model.joints?.find((j) => j.outType == "sag");
      //if (nextJoint && nextJoint.binded) return;

      const _meterZ = new SimpleMeter(meterSize.z);
      _meterZ.setDirection(new Vector3(0, 0, -1), new Vector3(0, 1, 0));
      _meterZ.setSize(meterSize.z);
      _meterZ.setSizeLabel(serviceOuter?.z ?? meterSize.z);
      _meterZ.position.setX(meterCenter.x - meterSize.x * 0.5 - marginSide);
      _meterZ.position.setZ(meterCenter.z + meterSize.z * 0.5);

      model.add(_meterZ);
      this._meters.push(_meterZ);
      this._align(
        Engine.activeEngine!.activeCamera!.instance as
          | OrthographicCamera
          | PerspectiveCamera,
      );
    });

    Engine.activeEngine!.dispatchCustomEvent("request-update");
  }

  private _addMeterItemsOfConfigurator(
    configurator: SugarModelConfigurator,
    yOffset: number,
    showParts: boolean = true,
  ) {
    configurator.sideModels.forEach((m) => {
      const { size, center } = this.getSize(m);
      //console.log("side model size", size);
      const _meterM = new SimpleMeter(size.x, configurator);
      _meterM.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterM.setSize(size.x);
      _meterM.position.setX(center.x - size.x * 0.5);
      _meterM.position.setZ(center.z - size.z * 0.5 - 0.02);
      _meterM.position.setY(center.y + size.y * 0.5 + 0.1);
      /*m.add(_meterM);
      m.updateWorldMatrix(true, true);
      model.attach(_meterM);
      _meterM.position.y = yOffset; // Adjust position for configurator models*/
      this._meters.push(_meterM);
    });

    configurator.models.forEach((model) => {
      const { size, center } = this.getSize(model);
      const service = this._serviceSize(model);

      const _meterM = new SimpleMeter(size.x, configurator);
      _meterM.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterM.setSize(size.x);
      _meterM.setSizeLabel(service.x ?? size.x);
      _meterM.position.setX(center.x - size.x * 0.5);
      _meterM.position.setZ(center.z - size.z * 0.5 - 0.02);
      _meterM.position.setY(center.y + size.y * 0.5 + 0.1);

      if (showParts) {
        model.add(_meterM);
        model.updateWorldMatrix(true, true);
        configurator.attach(_meterM);
      }
      _meterM.position.y = yOffset; // Adjust position for configurator models
      this._meters.push(_meterM);

      // Birden çok modülde modül başına derinlik çizilmez; birleşimin tek
      // derinliğini show() içindeki genel _meterZ verir.
      if (configurator.models.length > 1) return;

      const _meterZ = new SimpleMeter(size.z);
      _meterZ.setDirection(new Vector3(0, 0, -1), new Vector3(0, 1, 0));
      _meterZ.setSize(size.z);
      _meterZ.setSizeLabel(service.z ?? size.z);
      _meterZ.position.setX(center.x + size.x * 0.5 + 0.2);
      _meterZ.position.setZ(center.z + size.z * 0.5);

      if (showParts) {
        model.add(_meterZ);
        model.updateWorldMatrix(true, true);
        configurator.attach(_meterZ);
      }
      _meterZ.position.y = yOffset;
      this._meters.push(_meterZ);
    });
  }

  private _debugMeshes: Mesh[] = [];
  public debug = false;

  private _layoutFromJoints(configurator: SugarModelConfigurator):
    | {
        size: Vector3;
        /** Eksen bütün modüllerde servisten geldiyse true. */
        fromService: { x: boolean; y: boolean; z: boolean };
        layouts: {
          model: SugarModel;
          pos: Vector3;
          rot: Quaternion;
          localBox: Box3;
        }[];
      }
    | undefined {
    const fromService = { x: true, y: true, z: true };
    const sizeOf = (m: SugarModel): Vector3 | undefined => {
      const bbox = getLocalBoundingBox(m);
      const s = new Vector3();
      bbox?.getSize(s);
      const service = this._serviceSize(m);
      if (service.x !== undefined) s.x = service.x;
      else fromService.x = false;
      if (service.y !== undefined) s.y = service.y;
      else fromService.y = false;
      if (service.z !== undefined) s.z = service.z;
      else fromService.z = false;
      return s.x > 0 && s.y > 0 && s.z > 0 ? s : undefined;
    };

    const aabb = (s: Vector3) =>
      new Box3(
        new Vector3(-s.x / 2, 0, -s.z / 2),
        new Vector3(s.x / 2, s.y, s.z / 2),
      );
    // Joint face-center in model-local coords (along a unit direction).
    const faceCenter = (dir: Vector3, s: Vector3) =>
      new Vector3((dir.x * s.x) / 2, (dir.y * s.y) / 2, (dir.z * s.z) / 2);

    type Layout = {
      model: SugarModel;
      pos: Vector3;
      rot: Quaternion;
      localBox: Box3;
    };
    const layouts: Layout[] = [];
    const armOf: number[] = []; // parallel to layouts

    // 1) Walk the main-models joint chain. At each step, the outgoing
    //    joint's direction (rotated by current's orientation) tells us
    //    where the next module goes — so corners naturally turn.
    const firstMain = configurator.models.find((m) => !!sizeOf(m));
    if (!firstMain) return undefined;

    const visited = new Set<SugarModel>();
    let current: SugarModel | undefined = firstMain;
    let curPos = new Vector3();
    let curRot = new Quaternion();
    let inJoint: any | undefined;
    let firstOutDirW: Vector3 = new Vector3(1, 0, 0);
    let lastOutDirW: Vector3 = new Vector3(1, 0, 0);
    let firstOutSet = false;
    // Arm tracking: chain segments that don't change axis form an "arm".
    // Each arm has a back-wall direction (chainDir rotated +90° around Y).
    const armChainDirW: Vector3[] = [];
    let armIdx = 0;
    let prevOutDirW: Vector3 | undefined;


    while (current && !visited.has(current)) {
      visited.add(current);
      const s = sizeOf(current)!;
      layouts.push({
        model: current,
        pos: curPos.clone(),
        rot: curRot.clone(),
        localBox: aabb(s),
      });
      armOf.push(armIdx);

      const joints: any[] = current.joints ?? [];
      const next: any = joints.find(
        (j) =>
          j.binded &&
          j !== inJoint &&
          j.bindedJoint?.model &&
          !visited.has(j.bindedJoint.model) &&
          configurator.models.includes(j.bindedJoint.model),
      );
      if (!next?.bindedJoint) break;

      const outDirL = next.direction.clone().normalize();
      const outDirW = outDirL.clone().applyQuaternion(curRot).normalize();
      const outJointW = faceCenter(outDirL, s)
        .applyQuaternion(curRot)
        .add(curPos);

      if (!firstOutSet) {
        firstOutDirW = outDirW.clone();
        firstOutSet = true;
      }
      lastOutDirW = outDirW.clone();

      // Arm change detection: if outDirW axis differs from prev, new arm.
      if (prevOutDirW && Math.abs(outDirW.dot(prevOutDirW)) < 0.5) {
        armIdx++;
      }
      if (!armChainDirW[armIdx]) armChainDirW[armIdx] = outDirW.clone();
      prevOutDirW = outDirW.clone();

      const nextModel: SugarModel = next.bindedJoint.model;
      const nextS = sizeOf(nextModel);
      if (!nextS) break;
      const inDirL = next.bindedJoint.direction.clone().normalize();
      const nextRot = new Quaternion().setFromUnitVectors(
        inDirL,
        outDirW.clone().negate(),
      );
      const nextPos = outJointW
        .clone()
        .sub(faceCenter(inDirL, nextS).applyQuaternion(nextRot));

      curPos = nextPos;
      curRot = nextRot;
      inJoint = next.bindedJoint;
      current = nextModel;
    }

    // Default arm direction for single-piece chains.
    if (!armChainDirW[0]) armChainDirW[0] = new Vector3(1, 0, 0);

    // 2) Attach the two sideModels at the chain ends (opposite of the
    //    start's first outgoing direction, and along the end's last).
    const startMain = layouts[0];
    const endMain = layouts[layouts.length - 1];
    const sides = configurator.sideModels.filter((m) => !!sizeOf(m));
    const placeSide = (
      side: SugarModel,
      hostLayout: typeof layouts[number],
      outwardDirWorld: Vector3,
    ) => {
      const sideS = sizeOf(side)!;
      const hostS = sizeOf(hostLayout.model)!;
      // joint at host face center along outwardDirWorld
      const hostFaceW = faceCenter(
        outwardDirWorld.clone().applyQuaternion(hostLayout.rot.clone().invert()),
        hostS,
      )
        .applyQuaternion(hostLayout.rot)
        .add(hostLayout.pos);
      // side's center sits half its width past the face along outwardDir
      const sidePos = hostFaceW
        .clone()
        .add(outwardDirWorld.clone().multiplyScalar(sideS.x / 2));
      // align side's local X to outwardDir
      const sideRot = new Quaternion().setFromUnitVectors(
        new Vector3(1, 0, 0),
        outwardDirWorld,
      );
      layouts.push({
        model: side,
        pos: sidePos,
        rot: sideRot,
        localBox: aabb(sideS),
      });
      armOf.push(armOf[layouts.indexOf(hostLayout)] ?? 0);
    };

    if (sides[0] && startMain) {
      placeSide(sides[0], startMain, firstOutDirW.clone().negate());
    }
    if (sides[1] && endMain) {
      placeSide(sides[1], endMain, lastOutDirW.clone());
    }

    // 3) Outer size = per-arm sum of sizes along the arm's chain direction.
    //    The pivot (corner) belongs to both arms. Side modules inherit
    //    their host's arm. Perpendicular dim of an arm = its members' max
    //    depth along the back direction. We take the max chain length per
    //    world axis (X / Z) so an L-corner gives e.g. 167 x 167.
    // Project world direction into the module's LOCAL frame so a rotated
    // module reports its data.x along the new chain direction.
    const layoutByModel = new Map<SugarModel, Layout>();
    for (const l of layouts) layoutByModel.set(l.model, l);
    const sizeAlongAxis = (m: SugarModel, dirW: Vector3) => {
      const s = sizeOf(m)!;
      const rot = layoutByModel.get(m)?.rot ?? new Quaternion();
      const dirL = dirW.clone().applyQuaternion(rot.clone().invert());
      return Math.abs(dirL.x) * s.x + Math.abs(dirL.z) * s.z;
    };

    const armCount = Math.max(0, ...armOf) + 1;
    const armMembers: SugarModel[][] = [];
    for (let a = 0; a < armCount; a++) armMembers[a] = [];
    for (let i = 0; i < layouts.length; i++) {
      armMembers[armOf[i]].push(layouts[i].model);
    }
    // Share the pivot (last main model in arm a — sides don't pivot) with
    // arm a+1's front.
    const lastMainOfArm = (a: number): SugarModel | undefined => {
      for (let i = layouts.length - 1; i >= 0; i--) {
        if (
          armOf[i] === a &&
          configurator.models.includes(layouts[i].model)
        ) {
          return layouts[i].model;
        }
      }
      return undefined;
    };
    for (let a = 0; a < armCount - 1; a++) {
      const pivot = lastMainOfArm(a);
      if (pivot && armMembers[a + 1] && !armMembers[a + 1].includes(pivot)) {
        armMembers[a + 1].unshift(pivot);
      }
    }

    let outerX = 0,
      outerY = 0,
      outerZ = 0;
    for (let a = 0; a < armCount; a++) {
      const dir = armChainDirW[a] ?? new Vector3(1, 0, 0);
      const perp = new Vector3(dir.z, 0, -dir.x);
      const length = armMembers[a].reduce(
        (sum, m) => sum + sizeAlongAxis(m, dir),
        0,
      );
      const depth = Math.max(
        ...armMembers[a].map((m) => sizeAlongAxis(m, perp)),
      );
      if (Math.abs(dir.x) > 0.5) outerX = Math.max(outerX, length);
      if (Math.abs(dir.z) > 0.5) outerZ = Math.max(outerZ, length);
      if (Math.abs(perp.x) > 0.5) outerX = Math.max(outerX, depth);
      if (Math.abs(perp.z) > 0.5) outerZ = Math.max(outerZ, depth);
      for (const m of armMembers[a]) outerY = Math.max(outerY, sizeOf(m)!.y);
    }

    if (layouts.length === 0) return undefined;
    // mm snap → cm round, washes out FP drift.
    const cm = (v: number) => Math.round(Math.round(v * 1000) / 10) / 100;
    const size = new Vector3(cm(outerX), cm(outerY), cm(outerZ));
    return { size, fromService, layouts };
  }

  private _addDebugMeshes(
    configurator: SugarModelConfigurator,
    layouts: {
      model: SugarModel;
      pos: Vector3;
      rot: Quaternion;
      localBox: Box3;
    }[],
  ) {
    let added = 0;
    for (const { pos, rot, localBox } of layouts) {
      const size = new Vector3();
      const localCenter = new Vector3();
      localBox.getSize(size);
      localBox.getCenter(localCenter);
      if (size.x <= 0 || size.y <= 0 || size.z <= 0) continue;
      const mesh = new Mesh(
        new BoxGeometry(size.x, size.y, size.z),
        new MeshBasicMaterial({
          color: 0x00ff00,
          transparent: true,
          opacity: 0.35,
          depthTest: false,
          depthWrite: false,
        }),
      );
      mesh.renderOrder = 999;
      mesh.frustumCulled = false;
      mesh.userData.contributeToAO = "false";
      mesh.raycast = () => {};
      // BoxGeometry is centered on its origin; shift to localBox center in
      // model-local space, then transform to configurator-local space.
      const worldCenter = localCenter.clone().applyQuaternion(rot).add(pos);
      mesh.position.copy(worldCenter);
      mesh.quaternion.copy(rot);
      configurator.add(mesh);
      this._debugMeshes.push(mesh);
      added++;
    }
  }

  private _clearDebugMeshes() {
    for (const m of this._debugMeshes) {
      m.removeFromParent();
      (m.material as MeshBasicMaterial).dispose();
      m.geometry.dispose();
    }
    this._debugMeshes = [];
  }

  hide() {
    this._show = false;
    this._meters.forEach((m) => {
      m.dispose();
      m.removeFromParent();
    });

    this._meters = [];
    this._clearDebugMeshes();

    Engine.activeEngine?.dispatchCustomEvent("request-update");
  }

  toggle(models: SugarModel[]) {
    if (!this._show) {
      this.show(models);
    } else {
      this.hide();
    }
  }

  /** Ana gövde merkezi. sideModels (sandalye vb.) kutuya girmez. */
  private _mainBodyCenter(configurator: SugarModelConfigurator): Vector3 | undefined {
    const parts = configurator.models;
    if (!parts.length) return undefined;

    const skip = new Set<Object3D>(configurator.sideModels);
    configurator.updateMatrixWorld(true);
    const rootInverse = new Matrix4().copy(configurator.matrixWorld).invert();
    const box = new Box3();
    let any = false;

    const visit = (object: Object3D) => {
      if (skip.has(object)) return;
      const mesh = object as Mesh;
      if (mesh.isMesh && mesh.geometry) {
        if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
        const local = mesh.geometry.boundingBox!.clone();
        const meshToRoot = new Matrix4().multiplyMatrices(
          rootInverse,
          mesh.matrixWorld,
        );
        local.applyMatrix4(meshToRoot);
        box.union(local);
        any = true;
      }
      for (const child of object.children) visit(child);
    };

    for (const part of parts) visit(part);
    if (!any || box.isEmpty()) return undefined;

    return box.getCenter(new Vector3());
  }

  /** Servisten gelen ölçü (catalog_products x/y/z, cm) metre olarak; boş eksen undefined. */
  private _serviceSize(model: SugarModel) {
    const data = model.data as { x?: unknown; y?: unknown; z?: unknown } | undefined;
    const meters = (value: unknown) =>
      typeof value === "number" && value > 0 ? value / 100 : undefined;
    return { x: meters(data?.x), y: meters(data?.y), z: meters(data?.z) };
  }

  getSize(model: SugarModel) {
    const bound = getLocalBoundingBox(model);

    const size = new Vector3();
    bound?.getSize(size);
    const center = new Vector3();
    bound?.getCenter(center);

    const box = new Mesh(
      new BoxGeometry(size.x, size.y, size.z),
      new MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.8,
      }),
    );
    box.position.copy(center);

    return { size, center, box };
  }

  dispose(): void {
    window.removeEventListener(LENGTH_UNIT_CHANGED_EVENT, this._onUnitChanged);
    this.hide();
  }
}
